/**
 * React hook for managing Server-Sent Events (SSE) connection.
 *
 * Maintains a single persistent SSE connection for the authenticated user session.
 * Automatically reconnects on connection drops with exponential backoff.
 * Should be called once at app level (in ProtectedLayout.tsx).
 *
 * Design principle: ONE connection per user session.
 * - Opens when user is authenticated
 * - Stays open (reconnects on drops)
 * - Closes only on logout/unmount
 *
 * The activeJobs tracking is for UI purposes only - it does NOT affect connection lifecycle.
 */

import { useEffect, useRef } from 'react';
import { addToast } from '@heroui/react';
import { useQueryClient } from '@tanstack/react-query';
import { EventSource } from 'eventsource';

import { getApiConfig } from '~/config';
import { auth } from '~/config/firebase';
import { productKeys } from '~/hooks/useProducts';
import { useRefresh } from '~/hooks/useRefresh';
import { useSSEStore } from '~/stores/sseStore';
import { logger } from '~/utils/logger';
import { getConnectionRetryDelay, RETRY_STRATEGIES } from '~/utils/retry';

import type {
  CompletedEvent,
  ErrorEvent,
  PRDAssemblingCompleteEvent,
  PRDPhaseEvent,
  PRDProgressEvent,
  PRDReviewErrorEvent,
  PRDReviewMessageEvent,
  PRDReviewTypingEvent,
  PRDReviewUpdateEvent,
  ProcessingEvent,
  ProgressEvent,
  ProjectStatusChangedEvent,
  SSEEvent,
  TaskAddedEvent,
  TaskCreatedEvent,
  TaskPromptCreatedEvent,
  TaskPromptUpdatedEvent,
  TaskStatusChangedEvent,
} from '~/types';

/**
 * Type guard to check if event has projectId.
 *
 * System events (connection_established, heartbeat) don't have projectId.
 * Agent events (processing, progress, completed, error, task_*, prd_*) always have projectId.
 * PRD Review events (prd_review_*) also have projectId.
 * Status-changed events (task_status_changed, project_status_changed) also have projectId.
 * task_added also has projectId.
 */
function hasProjectId(
  event: SSEEvent,
): event is
  | ProcessingEvent
  | ProgressEvent
  | PRDProgressEvent
  | PRDPhaseEvent
  | PRDAssemblingCompleteEvent
  | CompletedEvent
  | ErrorEvent
  | TaskCreatedEvent
  | TaskPromptCreatedEvent
  | TaskPromptUpdatedEvent
  | PRDReviewTypingEvent
  | PRDReviewMessageEvent
  | PRDReviewUpdateEvent
  | PRDReviewErrorEvent
  | TaskStatusChangedEvent
  | ProjectStatusChangedEvent
  | TaskAddedEvent {
  return 'projectId' in event.data;
}

/**
 * Parse SSE event data from EventSource message.
 */
function parseSSEEvent(event: MessageEvent): SSEEvent | null {
  try {
    // Check if data exists and is not just whitespace
    if (!event.data || event.data.trim() === '') {
      logger.warn({ component: 'useSSEConnection' }, 'SSE event has empty data', {
        eventType: event.type,
      });
      return null;
    }

    const data = JSON.parse(event.data);

    return {
      event: event.type as SSEEvent['event'],
      id: event.lastEventId,
      data,
    };
  } catch (error) {
    logger.error(
      { component: 'useSSEConnection' },
      'Failed to parse SSE event',
      error instanceof Error ? error : new Error(String(error)),
      { eventType: event.type, rawData: event.data },
    );
    return null;
  }
}

// Extract max retries as a constant outside the component
// This is stable and doesn't need to be in dependency arrays
const MAX_RECONNECT_ATTEMPTS = RETRY_STRATEGIES.SSE_CONNECTION.maxRetries;

/**
 * Hook for managing SSE connection lifecycle.
 *
 * Maintains a persistent connection for the authenticated user.
 * Automatically reconnects on drops with exponential backoff.
 *
 * Should be called once in ProtectedLayout.tsx.
 *
 * @example
 * ```typescript
 * // In ProtectedLayout.tsx
 * useSSEConnection();
 * ```
 */
export function useSSEConnection(): void {
  const { refreshData } = useRefresh();
  const queryClient = useQueryClient();
  const setConnectionStatus = useSSEStore(state => state.setConnectionStatus);
  const addEvent = useSSEStore(state => state.addEvent);
  const updateJobProgress = useSSEStore(state => state.updateJobProgress);

  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectAttemptRef = useRef<number>(0);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isConnectingRef = useRef<boolean>(false);
  const isMountedRef = useRef<boolean>(true);
  const connectionIdRef = useRef<string | null>(null);

  useEffect(() => {
    isMountedRef.current = true;

    const connect = async (): Promise<void> => {
      // Don't connect if unmounted
      if (!isMountedRef.current) {
        return;
      }

      // Prevent concurrent connection attempts
      if (isConnectingRef.current || eventSourceRef.current) {
        return;
      }

      isConnectingRef.current = true;

      try {
        logger.info({ component: 'useSSEConnection' }, 'Attempting SSE connection', {
          attempt: reconnectAttemptRef.current + 1,
          maxAttempts: MAX_RECONNECT_ATTEMPTS,
        });

        setConnectionStatus('connecting');

        // Get Firebase auth token
        const user = auth.currentUser;
        if (!user) {
          // Auth not ready yet - retry after delay
          logger.debug({ component: 'useSSEConnection' }, 'Auth not ready yet, will retry');
          isConnectingRef.current = false;

          const delay = getConnectionRetryDelay(
            reconnectAttemptRef.current,
            RETRY_STRATEGIES.SSE_CONNECTION,
          );
          reconnectAttemptRef.current += 1;
          reconnectTimeoutRef.current = setTimeout(() => {
            void connect();
          }, delay);
          return;
        }

        const token = await user.getIdToken();
        const config = getApiConfig();
        const url = `${config.baseURL}/sse`;

        // Create EventSource with Authorization header
        const eventSource = new EventSource(url, {
          fetch: async (input, init): Promise<Response> => {
            const response = await fetch(input, {
              ...init,
              headers: {
                ...init.headers,
                Authorization: `Bearer ${token}`,
              },
            });

            // Handle 429 (max connections) specially with longer backoff
            if (response.status === 429) {
              logger.warn(
                { component: 'useSSEConnection' },
                'SSE connection rejected with 429 (max connections exceeded)',
                {
                  reconnectAttempt: reconnectAttemptRef.current,
                  willRetryIn: '10 seconds',
                },
              );

              // Don't return the 429 response - throw to trigger error handler
              // This prevents EventSource from trying to process it
              throw new Error('SSE_CONNECTION_LIMIT_EXCEEDED');
            }

            return response;
          },
        });

        eventSourceRef.current = eventSource;
        setConnectionStatus('connected');

        // Invalidate all streamed-data caches on connect/reconnect so updates missed
        // while disconnected aren't left stale (frontend-sse-consumption: reconnect-cache-invalidation).
        void queryClient.invalidateQueries({ queryKey: ['projects'] });
        void queryClient.invalidateQueries({ queryKey: ['project'] });
        void queryClient.invalidateQueries({ queryKey: ['tasks'] });
        void queryClient.invalidateQueries({ queryKey: ['task'] });
        void queryClient.invalidateQueries({ queryKey: productKeys.all });

        reconnectAttemptRef.current = 0; // Reset on successful connection
        isConnectingRef.current = false;

        logger.info({ component: 'useSSEConnection' }, 'SSE connection established');

        // Handle errors - reconnect with backoff
        eventSource.addEventListener('error', event => {
          logger.warn({ component: 'useSSEConnection' }, 'SSE connection error, will reconnect', {
            readyState: eventSource.readyState,
            eventType: event.type,
          });

          // Close the failed connection
          eventSource.close();
          eventSourceRef.current = null;
          isConnectingRef.current = false;
          setConnectionStatus('error');

          // Don't reconnect if unmounted
          if (!isMountedRef.current) {
            return;
          }

          reconnectAttemptRef.current += 1;

          // Check if we've exhausted retries
          if (reconnectAttemptRef.current >= MAX_RECONNECT_ATTEMPTS) {
            logger.error(
              { component: 'useSSEConnection' },
              'Max reconnection attempts reached',
              new Error('Max SSE reconnection attempts exceeded'),
              { attempts: reconnectAttemptRef.current },
            );
            addToast({
              color: 'danger',
              description: 'Lost connection to server. Please refresh the page.',
            });
            return;
          }

          // Schedule reconnection with exponential backoff
          const delay = getConnectionRetryDelay(
            reconnectAttemptRef.current - 1,
            RETRY_STRATEGIES.SSE_CONNECTION,
          );

          logger.info({ component: 'useSSEConnection' }, 'Scheduling reconnection', {
            delayMs: delay,
            attempt: reconnectAttemptRef.current,
            maxAttempts: MAX_RECONNECT_ATTEMPTS,
          });

          reconnectTimeoutRef.current = setTimeout(() => {
            void connect();
          }, delay);
        });

        // Listen for all event types
        const eventTypes = [
          'connection_established',
          'heartbeat',
          'processing',
          'progress',
          'prd_progress',
          'prd_phase',
          'prd_assembling_complete',
          'completed',
          'error',
          'task_created',
          'task_prompt_created',
          'task_prompt_updated',
          'prd_review_typing',
          'prd_review_message',
          'prd_review_update',
          'prd_review_error',
          'task_status_changed',
          'project_status_changed',
          'task_added',
        ];

        for (const eventType of eventTypes) {
          eventSource.addEventListener(eventType, event => {
            const parsed = parseSSEEvent(event);
            if (!parsed) {
              return;
            }

            // Handle connection_established to capture connectionId for explicit disconnect
            if (parsed.event === 'connection_established') {
              connectionIdRef.current = parsed.data.connectionId;
              logger.info({ component: 'useSSEConnection' }, 'Connection ID captured', {
                connectionId: parsed.data.connectionId,
              });
              return;
            }

            // Skip heartbeat events
            if (parsed.event === 'heartbeat') {
              return;
            }

            // Add to store (all non-system events have projectId)
            if (hasProjectId(parsed)) {
              addEvent(parsed.data.projectId, parsed);

              // Trigger background data refresh when a task is created
              if (parsed.event === 'task_created') {
                logger.info(
                  { component: 'useSSEConnection' },
                  'Task created event received, triggering refresh',
                  {
                    projectId: parsed.data.projectId,
                    taskId: parsed.data.task_id,
                    title: parsed.data.task_title,
                  },
                );
                void refreshData(['tasks']);
              }

              // Invalidate session status on error events for immediate UI update
              if (parsed.event === 'error' || parsed.event === 'prd_review_error') {
                logger.info(
                  { component: 'useSSEConnection' },
                  'Error event received, invalidating session status',
                  {
                    projectId: parsed.data.projectId,
                    errorType: 'error_type' in parsed.data ? parsed.data.error_type : 'unknown',
                    message: 'message' in parsed.data ? parsed.data.message : undefined,
                    errorMessage:
                      'errorMessage' in parsed.data ? parsed.data.errorMessage : undefined,
                  },
                );
                void queryClient.invalidateQueries({ queryKey: ['prd-session-status-batch'] });
                void queryClient.invalidateQueries({ queryKey: ['projects'] });
              }

              // Update job progress for progress events
              if (
                (parsed.event === 'progress' || parsed.event === 'prd_progress') &&
                'progress' in parsed.data
              ) {
                // Get fresh state from store to avoid stale closure
                const currentJobs = useSSEStore.getState().activeJobs;
                const jobId = Object.keys(currentJobs).find(
                  id => currentJobs[id]?.projectId === parsed.data.projectId,
                );
                if (jobId && typeof parsed.data.progress === 'number') {
                  updateJobProgress(jobId, parsed.data.progress);
                }
              }

              // Invalidate task and project caches on task status change
              if (parsed.event === 'task_status_changed') {
                logger.info(
                  { component: 'useSSEConnection' },
                  'Task status changed event received, invalidating caches',
                  {
                    projectId: parsed.data.projectId,
                    taskId: parsed.data.taskId,
                    status: parsed.data.status,
                  },
                );
                void queryClient.invalidateQueries({ queryKey: ['tasks', parsed.data.projectId] });
                void queryClient.invalidateQueries({ queryKey: ['projects'] });
                void queryClient.invalidateQueries({
                  queryKey: ['task', parsed.data.projectId, parsed.data.taskId],
                });
                void queryClient.invalidateQueries({ queryKey: productKeys.all });
              }

              // Invalidate task and project caches when a new task is added, so an open
              // Project Board's task count/progress bar refreshes live. No single-task-detail
              // (['task', projectId, taskId]) invalidation - a brand-new task has no existing
              // detail-cache entry to invalidate.
              if (parsed.event === 'task_added') {
                logger.info(
                  { component: 'useSSEConnection' },
                  'Task added event received, invalidating caches',
                  {
                    projectId: parsed.data.projectId,
                    taskId: parsed.data.taskId,
                    title: parsed.data.title,
                  },
                );
                void queryClient.invalidateQueries({ queryKey: ['tasks', parsed.data.projectId] });
                void queryClient.invalidateQueries({ queryKey: ['projects'] });
                void queryClient.invalidateQueries({ queryKey: productKeys.all });
              }

              // Invalidate project caches on project status change
              if (parsed.event === 'project_status_changed') {
                logger.info(
                  { component: 'useSSEConnection' },
                  'Project status changed event received, invalidating caches',
                  {
                    projectId: parsed.data.projectId,
                    status: parsed.data.status,
                  },
                );
                void queryClient.invalidateQueries({ queryKey: ['projects'] });
                void queryClient.invalidateQueries({
                  queryKey: ['project', parsed.data.projectId],
                });
                void queryClient.invalidateQueries({ queryKey: productKeys.all });
              }
            }
          });
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        const is429Error = errorMessage.includes('SSE_CONNECTION_LIMIT_EXCEEDED');

        logger.error(
          { component: 'useSSEConnection' },
          is429Error ? 'SSE connection limit exceeded (429)' : 'Failed to connect to SSE',
          error instanceof Error ? error : new Error(String(error)),
          { attempt: reconnectAttemptRef.current, is429Error },
        );
        setConnectionStatus('error');
        isConnectingRef.current = false;

        // Don't reconnect if unmounted
        if (!isMountedRef.current) {
          return;
        }

        // For 429 errors, use a much longer backoff to give backend time to clean up connections
        if (is429Error) {
          const connectionLimitBackoff = 10000; // 10 seconds fixed delay for 429

          logger.info(
            { component: 'useSSEConnection' },
            'Connection limit hit, waiting for backend cleanup',
            {
              delayMs: connectionLimitBackoff,
              attempt: reconnectAttemptRef.current + 1,
              maxAttempts: MAX_RECONNECT_ATTEMPTS,
            },
          );

          // Don't increment attempt counter for 429 - these aren't "failed connections"
          // The connection was rejected due to pool exhaustion, not a connection failure
          reconnectTimeoutRef.current = setTimeout(() => {
            void connect();
          }, connectionLimitBackoff);
          return;
        }

        // For other errors, use normal exponential backoff
        if (reconnectAttemptRef.current < MAX_RECONNECT_ATTEMPTS) {
          const delay = getConnectionRetryDelay(
            reconnectAttemptRef.current,
            RETRY_STRATEGIES.SSE_CONNECTION,
          );
          reconnectAttemptRef.current += 1;

          logger.info({ component: 'useSSEConnection' }, 'Scheduling reconnection', {
            delayMs: delay,
            attempt: reconnectAttemptRef.current,
            maxAttempts: MAX_RECONNECT_ATTEMPTS,
          });

          reconnectTimeoutRef.current = setTimeout(() => {
            void connect();
          }, delay);
        }
      }
    };

    // Start connection immediately
    void connect();

    // Add beforeunload handler to explicitly disconnect on page reload/close
    const handleBeforeUnload = (): void => {
      const connectionId = connectionIdRef.current;
      if (connectionId) {
        // Use sendBeacon for reliable disconnect notification during page unload
        // This runs synchronously and doesn't get cancelled by page navigation
        const config = getApiConfig();
        const url = `${config.baseURL}/sse/connections/${connectionId}`;

        // sendBeacon doesn't support DELETE, so we need to use fetch with keepalive
        // keepalive ensures request completes even after page unloads
        const user = auth.currentUser;
        if (user) {
          user
            .getIdToken()
            .then(token => {
              void fetch(url, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` },
                keepalive: true, // Critical: ensures request completes during unload
              }).catch(() => {
                // Ignore errors during unload - connection will be cleaned up eventually
              });
            })
            .catch(() => {
              // Ignore token fetch errors during unload
            });
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup on unmount only
    return (): void => {
      isMountedRef.current = false;

      // Remove beforeunload handler
      window.removeEventListener('beforeunload', handleBeforeUnload);

      // Clear any pending reconnection attempts
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      if (eventSourceRef.current) {
        logger.info({ component: 'useSSEConnection' }, 'Closing SSE connection on unmount');
        eventSourceRef.current.close();
        eventSourceRef.current = null;
        setConnectionStatus('disconnected');
      }

      isConnectingRef.current = false;

      // Also send explicit disconnect if we have a connection ID
      const connectionId = connectionIdRef.current;
      if (connectionId) {
        handleBeforeUnload();
      }
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps --
       SSE connection must run ONCE on mount and stay open for entire session.
       Adding dependencies would cause reconnection on every state change (especially 
       refreshData which changes when isOnline/isRefreshing changes), which is undesirable 
       for long-lived connections. All captured values are stable (Zustand store functions, 
       queryClient) or refs (eventSourceRef, reconnectAttemptRef, etc.). */
  }, []);
}
